import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Modal, Segmented } from 'antd';
import { Copy } from '@phosphor-icons/react';
import config from '../../config';
import { useAuthUser } from '../../context/AuthContext';
import useSandbox from '../../hooks/useSandbox';
import ConnectionCheck from './ConnectionCheck';
import SupportedStacksLink from '../stacks/SupportedStacksLink';
import ToolIcon from './ToolIcon';
import './sandbox.css';

/** The sandbox's MCP endpoint, the one address every AI tool connects to. */
export function sandboxMcpUrl() {
  const base = (config.publicBaseUrl || window.location.origin).replace(/\/$/, '');
  return `${base}/sandbox/mcp`;
}

/**
 * The Claude Code plugin's marketplace that this Flow serves (a read-only git repo, vfs-service
 * src/gateway/claudeMarketplace.js), next to its MCP endpoint: for personal accounts, without an organization library.
 */
export function claudePluginMarketplaceUrl(mcpUrl) {
  return `${mcpUrl.replace(/\/sandbox\/mcp\/?$/, '')}/sandbox/plugin/claude-code.git`;
}

// GitHub Copilot (VS Code, Visual Studio, the CLI) in a company: the organization's Copilot policies decide.
export const COPILOT_POLICY_NOTE = 'If your company uses Copilot Business or Enterprise, an admin must allow MCP servers in the Copilot policies (and add the sandbox URL to the MCP allowlist/registry if one is set).';
// Microsoft 365 Copilot and Copilot Studio: Connections › Organization AI connections (admins) sets them up.
const ADMIN_SETS_UP = 'Your Flow admin sets this up once for the organization; then:';
const PLUGIN_TREE = 'https://git.tools.mia-platform.eu/platform/flow/mia-sandbox-agentic-plugin/-/tree/main';

/** Cursor's one-click MCP install link for the sandbox server. */
export function cursorInstallUrl(url) {
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=mia-platform-flow&config=${btoa(JSON.stringify({ url }))}`;
}

/** VS Code's one-click MCP install link for the sandbox server. */
export function vscodeInstallUrl(url) {
  return `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: 'mia-platform-flow', type: 'http', url }))}`;
}

/** True when the sandbox URL only works on this machine: cloud-hosted tools (ChatGPT, Claude apps) can't reach it. */
export function isLocalUrl(url) {
  try {
    const { hostname } = new URL(url);
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname.endsWith('.localhost');
  } catch {
    return false;
  }
}

/** The plugin repo folder with a tool's rules and commands. */
export function rulesPackUrl(folder) {
  return `${PLUGIN_TREE}/${folder}`;
}

/** What every tool's "check it works" step asks: answered by the sandbox, so it proves the connection and the sign-in. */
export const CHECK_PROMPT = 'list my Flow projects';

/** Who to ask when an account is not let in: the configured Flow admin contact, if any. */
function askAdmin() {
  return config.adminContact ? `ask your Flow admin (${config.adminContact})` : 'ask your Flow admin';
}

function hostOf(url) {
  try { return new URL(url).host; } catch { return url; }
}

/** Troubleshooting that holds for every tool, after the tool's own. */
export function commonTroubleshooting(url) {
  return [
    `The browser shows an error or “access denied”: this account is not allowed on Flow. Sign in with the account you use for Flow, or ${askAdmin()} to invite you.`,
    `A corporate proxy or VPN can block ${hostOf(url)}: check that this address opens in your browser on the same network, or ask your IT team to allow it.`,
  ];
}

// The Claude apps' "authentication failed right after Connect": the gateway could not register Claude as an OAuth client.
export const CLAUDE_APPS_REGISTRATION_HINT = 'If Claude says “authentication failed” right after Connect, your Flow can’t register Claude yet. Ask your Flow admin: the sign-in realm must trust claude.ai and claude.com, or Flow’s own client registration must be on (flow-helm-chart docs/mcp-oauth.md).';

const CLAUDE_CODE_ORG_PLUGIN = 'claude plugin marketplace update claudeai-organization-library\nclaude plugin install mia-platform-flow@claudeai-organization-library';

/**
 * Every AI tool the sandbox plugin supports (mia-sandbox-agentic-plugin's README), how to set it up, and the MCP
 * client kinds the sandbox records for it (vfs-service src/lib/aiClients.js), so Flow can tell when it connected.
 *
 * A tool's setup is numbered steps (setupSteps): `steps(url)` adds Flow to the tool, then come "Sign in" (`signIn`,
 * with the same account as Flow) and "Check it works" (the live connection check). A tool with `complete: true`
 * writes all of its steps itself. `variants` (`[{ key, label, steps, signIn?, complete? }]`, the first is the default)
 * are two ways to set it up, e.g. "my company added Flow" and "add it yourself". A step is
 * `{ title?, text?, code?, copy?, after?, note?, account?, check?, options? }`: `code` gets a Copy button (copying
 * `copy` when set), `account` shows which account to sign in with, `check` shows the live check, `options` are
 * labelled alternatives (`{ label, text?, code?, note? }`). `troubleshooting(url)` and `notes` add to the setup.
 */
export const AI_TOOLS = [
  {
    key: 'claude-code',
    vendor: 'anthropic',
    icon: 'claude',
    name: 'Claude Code',
    sub: 'Terminal: server, optional plugin',
    kinds: ['claude-code'],
    what: 'Mia-Platform Flow in Claude Code, the terminal agent (any plan: Pro, Max, Team, Enterprise). Not the Claude desktop app or claude.ai: for those pick Claude apps.',
    complete: true,
    steps: (url) => [
      {
        title: 'Add Flow to Claude Code, for every folder',
        text: 'In a terminal, run:',
        code: `claude mcp add --transport http --scope user mia-platform-flow ${url}`,
        note: 'If you added it before without --scope user, first run: claude mcp remove mia-platform-flow',
      },
      {
        title: 'Sign in',
        text: 'Start claude, type /mcp, choose mia-platform-flow, then Authenticate. A browser opens:',
        account: true,
        after: 'Come back to the terminal: /mcp shows mia-platform-flow as connected.',
      },
      { title: 'Check it works', text: `Ask Claude: “${CHECK_PROMPT}”.`, check: true },
      {
        title: 'Optional: the Mia-Platform Flow plugin (the flow-sandbox skill and the /mia-platform-flow: commands)',
        text: 'Everything works without it. Pick the way that fits your account:',
        options: [
          {
            label: 'Your company publishes it in Claude (Team or Enterprise)',
            code: CLAUDE_CODE_ORG_PLUGIN,
            note: 'If claudeai-organization-library is missing from claude plugin marketplace list, first run: claude plugin marketplace add --claudeai claudeai-organization-library',
          },
          {
            label: 'Personal account (Pro or Max), or no company library',
            text: 'There is no organization library for a personal account, so the commands above do not work. Install it from Flow instead:',
            code: `claude plugin marketplace add ${claudePluginMarketplaceUrl(url)}\nclaude plugin install mia-platform-flow@mia-platform`,
            note: 'The plugin connects to this Flow. Get a newer version later with: claude plugin marketplace update mia-platform',
          },
        ],
        after: 'The plugin brings its own Flow server, mia-platform-flow. After installing it, remove the one from step 1 so Flow is not loaded twice (claude mcp remove --scope user mia-platform-flow), restart claude, and in /mcp choose mia-platform-flow, then Authenticate.',
      },
    ],
    troubleshooting: () => [
      '/mcp still shows “needs authentication”: choose mia-platform-flow, then Authenticate again.',
      'Run claude mcp list in a terminal: mia-platform-flow must be listed. If it is not, run step 1 again; if it shows only in one folder, it was added without --scope user.',
      'Remove it and add it again: claude mcp remove mia-platform-flow, then step 1.',
      'Update Claude Code (claude update): older versions can’t sign in to remote servers.',
    ],
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'claude',
    vendor: 'anthropic',
    icon: 'claude',
    name: 'Claude apps',
    sub: 'Desktop (Windows, Mac), web, mobile',
    kinds: ['claude-app'],
    cloud: true,
    what: 'Mia-Platform Flow as a connector in the Claude desktop app (Windows, Mac) and claude.ai; it then works in the mobile app too. Not Claude Code in the terminal: for that pick Claude Code.',
    variants: [
      {
        key: 'own',
        label: 'Personal account (Free, Pro, Max), or my company hasn’t added Flow',
        complete: true,
        steps: (url) => [
          {
            title: 'Open the connectors',
            text: 'In Claude (the desktop app or claude.ai): Settings › Connectors › Add custom connector.',
            note: 'If you don’t see Add custom connector, your plan or your organization doesn’t allow it: ask your Claude admin. On Team and Enterprise, an owner adds it once in Organization settings › Connectors; then everyone sees it under Connectors (use “My company added Flow to Claude” above).',
          },
          {
            title: 'Add Flow',
            text: 'Name: Mia-Platform Flow. URL:',
            code: url,
            after: 'Leave OAuth Client ID and OAuth Client Secret empty (under Advanced settings), then click Add.',
          },
          {
            title: 'Sign in',
            text: 'Click Connect next to Mia-Platform Flow. A browser window opens:',
            account: true,
            after: 'Allow access, then go back to Claude.',
          },
          { title: 'Check it works', text: `In a chat, turn on Mia-Platform Flow in the chat’s tools menu, then ask: “${CHECK_PROMPT}”.`, check: true },
        ],
      },
      {
        key: 'org',
        label: 'My company added Flow to Claude',
        complete: true,
        steps: () => [
          { title: 'Connect', text: 'In Claude (the desktop app or claude.ai): Settings › Connectors › Mia-Platform Flow › Connect.' },
          { title: 'Sign in', text: 'A browser window opens:', account: true, after: 'Allow access, then go back to Claude.' },
          { title: 'Check it works', text: `In a chat, turn on Mia-Platform Flow in the chat’s tools menu, then ask: “${CHECK_PROMPT}”.`, check: true },
        ],
      },
    ],
    notes: [
      'On Windows and Mac the desktop app opens your default browser: if you use Flow in another browser or profile, sign in there anyway, with the same account. If no window opens, allow pop-ups for Claude and click Connect again.',
    ],
    notConnectedHint: CLAUDE_APPS_REGISTRATION_HINT,
    troubleshooting: () => [
      CLAUDE_APPS_REGISTRATION_HINT,
      'Remove the connector in Settings › Connectors and add it again, then Connect.',
    ],
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'gemini-cli',
    vendor: 'google',
    icon: 'google',
    name: 'Gemini CLI',
    sub: 'Server',
    kinds: ['gemini-cli'],
    pack: 'gemini-cli',
    what: 'The sandbox server in Gemini CLI, Google’s terminal agent. Its extension (GEMINI.md and the /mia-platform-flow: commands) is in the plugin repo’s gemini-cli/ folder.',
    steps: (url) => [
      {
        title: 'Add Flow to Gemini CLI, for every folder',
        text: 'In a terminal, run:',
        code: `gemini mcp add --transport http --scope user mia-platform-flow ${url}`,
        note: 'Without --scope user it is added to the current project only.',
      },
    ],
    signIn: 'Start gemini and run /mcp auth mia-platform-flow. A browser opens:',
    troubleshooting: () => ['Run /mcp in gemini: mia-platform-flow must be listed as connected; /mcp auth mia-platform-flow signs in again.'],
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'antigravity',
    vendor: 'google',
    icon: 'google',
    name: 'Antigravity',
    sub: 'Server',
    kinds: ['antigravity'],
    what: 'The sandbox server in Google Antigravity’s agent.',
    steps: (url) => [
      { text: 'In Antigravity: Agent panel › … › MCP Servers › Manage MCP Servers › View raw config, and add to mcp_config.json:', code: JSON.stringify({ mcpServers: { 'mia-platform-flow': { serverUrl: url } } }, null, 2) },
    ],
    signIn: 'Antigravity asks you to sign in the first time it calls the sandbox. A browser opens:',
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'copilot-cli',
    vendor: 'microsoft',
    name: 'GitHub Copilot CLI',
    icon: 'githubcopilot',
    sub: 'Server',
    kinds: ['copilot-cli'],
    pack: 'vscode-copilot',
    what: 'The sandbox server in GitHub Copilot’s terminal agent (copilot). The instructions in the plugin repo’s vscode-copilot/ folder work here too.',
    steps: (url) => [
      { text: 'In Copilot CLI, run /mcp add and fill in (it is saved for every folder):', code: `Server name: mia-platform-flow\nServer type: HTTP\nURL: ${url}\nTools: *` },
      { text: 'Or add to ~/.copilot/mcp-config.json:', code: JSON.stringify({ mcpServers: { 'mia-platform-flow': { type: 'http', url, tools: ['*'] } } }, null, 2) },
    ],
    signIn: 'Copilot CLI asks you to sign in the first time it calls the sandbox (keep it up to date: npm install -g @github/copilot). A browser opens:',
    note: COPILOT_POLICY_NOTE,
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'cursor',
    vendor: 'cursor',
    icon: 'cursor',
    name: 'Cursor',
    sub: 'Server + rules',
    kinds: ['cursor'],
    oneClick: { label: 'Install in Cursor', href: cursorInstallUrl },
    pack: 'cursor',
    what: 'The sandbox server; the rules and the /mia-platform-flow-new-app command are in the plugin repo’s cursor/ folder.',
    steps: (url) => [{ text: 'Add to ~/.cursor/mcp.json in your home folder (every project; a project’s .cursor/mcp.json works in that project only):', code: JSON.stringify({ mcpServers: { 'mia-platform-flow': { url } } }, null, 2) }],
    signIn: 'In Cursor’s MCP settings, click the sign-in (Connect) button next to mia-platform-flow if Cursor does not ask by itself. A browser opens:',
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'vscode',
    vendor: 'microsoft',
    icon: 'githubcopilot',
    name: 'VS Code + Copilot',
    sub: 'Server + instructions',
    kinds: ['vscode'],
    oneClick: { label: 'Install in VS Code', href: vscodeInstallUrl },
    pack: 'vscode-copilot',
    what: 'The sandbox server; instructions and the /mia-platform-flow-new-app prompt are in the plugin repo’s vscode-copilot/ folder.',
    steps: (url) => [{ text: 'Add to .vscode/mcp.json (this workspace), or run MCP: Open User Configuration and add it there (every workspace):', code: JSON.stringify({ servers: { 'mia-platform-flow': { type: 'http', url } } }, null, 2) }],
    signIn: 'Copilot asks you to sign in the first time it calls the sandbox (agent mode). A browser opens:',
    note: COPILOT_POLICY_NOTE,
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'visual-studio',
    vendor: 'microsoft',
    icon: 'githubcopilot',
    name: 'Visual Studio',
    sub: '2026, 2022 17.14+',
    kinds: ['visual-studio'],
    what: 'The sandbox server in GitHub Copilot’s agent mode in Visual Studio 2026 or Visual Studio 2022 17.14 and later.',
    steps: (url) => [
      { text: 'Add to %USERPROFILE%\\.mcp.json (every solution) or <solution>\\.mcp.json (this solution):', code: JSON.stringify({ servers: { 'mia-platform-flow': { url } } }, null, 2) },
    ],
    signIn: 'In .mcp.json, click Authentication Required (or Manage Authentication) in the CodeLens above mia-platform-flow, then switch Copilot Chat to Agent. A browser opens:',
    note: COPILOT_POLICY_NOTE,
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'm365-copilot',
    vendor: 'microsoft',
    name: 'Microsoft 365 Copilot',
    sub: 'Agent, also in Teams',
    kinds: ['microsoft-365-copilot'],
    cloud: true,
    what: 'The Mia-Platform Flow agent in Microsoft 365 Copilot and in Teams, with the sandbox server as its tools. Your organization needs Microsoft 365 Copilot.',
    steps: () => [
      { text: `${ADMIN_SETS_UP} in Microsoft 365 Copilot (or Copilot in Teams):`, code: 'Copilot › Agents › Mia-Platform Flow' },
    ],
    signIn: 'The first time, Copilot asks you to sign in to Mia-Platform Flow:',
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'copilot-studio',
    vendor: 'microsoft',
    name: 'Copilot Studio',
    sub: 'MCP tool in your agents',
    kinds: ['copilot-studio'],
    cloud: true,
    what: 'The sandbox server as a Model Context Protocol tool of an agent you build in Microsoft Copilot Studio.',
    steps: (url) => [
      { text: `${ADMIN_SETS_UP} in Copilot Studio, open your agent and add the tool with the details your admin gives you:`, code: `Tools › Add a tool › New tool › Model Context Protocol\n  Server URL: ${url}\n  Authentication: OAuth 2.0 › Manual` },
    ],
    signIn: 'Copilot Studio asks each person to sign in the first time the agent uses the tool:',
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'windsurf',
    vendor: 'windsurf',
    icon: 'windsurf',
    name: 'Windsurf',
    sub: 'Server + workflows',
    kinds: ['windsurf'],
    pack: 'windsurf',
    what: 'The sandbox server; rules and workflows are in the plugin repo’s windsurf/ folder.',
    steps: (url) => [{ text: 'Merge into ~/.codeium/windsurf/mcp_config.json (every workspace), then refresh the MCP servers in Cascade:', code: JSON.stringify({ mcpServers: { 'mia-platform-flow': { serverUrl: url } } }, null, 2) }],
    signIn: 'Windsurf asks you to sign in the first time it calls the sandbox. A browser opens:',
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'codex',
    vendor: 'openai',
    name: 'OpenAI Codex',
    sub: 'Server + AGENTS.md',
    kinds: ['codex'],
    pack: 'codex',
    what: 'The sandbox server; AGENTS.md and prompts are in the plugin repo’s codex/ folder.',
    steps: (url) => [{ text: 'Append to ~/.codex/config.toml in your home folder (every project):', code: `[mcp_servers.mia-platform-flow]\nurl = "${url}"` }],
    signIn: 'In a terminal, run codex mcp login mia-platform-flow. A browser opens:',
    troubleshooting: () => ['Run codex mcp list: mia-platform-flow must be listed. Update Codex if it does not know codex mcp login.'],
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'chatgpt',
    vendor: 'openai',
    name: 'ChatGPT',
    sub: 'Connector',
    kinds: ['chatgpt'],
    cloud: true,
    what: 'The sandbox server as a ChatGPT connector.',
    variants: [
      {
        key: 'own',
        label: 'Add it myself (developer mode)',
        steps: (url) => [
          { text: 'Needs a plan with developer mode (Plus, Pro, Business, Enterprise or Edu); in a workspace an admin may have to allow it. In ChatGPT, turn on developer mode:', code: 'Settings › Apps & Connectors › Advanced settings › Developer mode' },
          { text: 'Then create the connector (if the menus differ, look for custom connectors in ChatGPT’s connector settings and add one with this URL):', code: `Settings › Apps & Connectors › Create\n  Name: mia-platform-flow\n  MCP server URL: ${url}\n  Authentication: OAuth` },
        ],
        signIn: 'ChatGPT opens a sign-in:',
      },
      {
        key: 'org',
        label: 'My workspace added Flow to ChatGPT',
        steps: () => [{ text: 'In ChatGPT’s connector settings, find Mia-Platform Flow (added by your workspace admin) and connect it.' }],
        signIn: 'ChatGPT opens a sign-in:',
      },
    ],
    checkText: `In a chat, enable mia-platform-flow (in developer mode, pick it in the chat’s tools), then ask: “${CHECK_PROMPT}”.`,
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'mistral',
    vendor: 'mistral',
    name: 'Mistral',
    icon: 'mistralai',
    sub: 'Le Chat connector, Vibe CLI',
    kinds: ['mistral'],
    cloud: true,
    what: 'The sandbox server as a custom MCP connector in Le Chat (on the web it connects from Mistral’s cloud), or in Mistral Vibe, Mistral’s terminal agent.',
    variants: [
      {
        key: 'own',
        label: 'Add it myself',
        steps: (url) => [
          { text: 'In Le Chat, add a custom connector (in an organization an admin may have to allow custom connectors):', code: `Intelligence › Connectors › Add connector › Custom MCP connector\n  Name: mia-platform-flow\n  Server URL: ${url}\n  Authentication: OAuth` },
          { text: 'Or in Mistral Vibe, add to ~/.vibe/config.toml (every folder):', code: `[[mcp_servers]]\nname = "mia-platform-flow"\ntransport = "streamable-http"\nurl = "${url}"` },
        ],
        signIn: 'Le Chat opens a sign-in; Vibe asks the first time it calls the sandbox. A browser opens:',
      },
      {
        key: 'org',
        label: 'My organization added Flow to Le Chat',
        steps: () => [{ text: 'In Le Chat’s connectors, find Mia-Platform Flow (added by your organization admin) and connect it.' }],
        signIn: 'Le Chat opens a sign-in:',
      },
    ],
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'junie',
    vendor: 'jetbrains',
    icon: 'jetbrains',
    name: 'JetBrains (Junie)',
    sub: 'Server + guidelines',
    kinds: ['jetbrains'],
    pack: 'jetbrains-junie',
    what: 'The sandbox server; .junie/guidelines.md is in the plugin repo’s jetbrains-junie/ folder.',
    steps: (url) => [{ text: 'In Junie › MCP settings, add a remote (HTTP) server with this URL; if Junie offers a choice, add it for all projects:', code: url }],
    signIn: 'Junie asks you to sign in the first time it calls the sandbox. A browser opens:',
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'kiro',
    vendor: 'aws',
    name: 'Kiro',
    sub: 'Server',
    kinds: ['kiro'],
    what: 'The sandbox server. A Kiro package of steering rules is not available yet.',
    steps: (url) => [{ text: 'Add to ~/.kiro/settings/mcp.json (every workspace) or .kiro/settings/mcp.json (this workspace only):', code: JSON.stringify({ mcpServers: { 'mia-platform-flow': { url } } }, null, 2) }],
    signIn: 'Kiro asks you to sign in the first time it calls the sandbox. A browser opens:',
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
  {
    key: 'other',
    vendor: 'other',
    icon: 'modelcontextprotocol',
    name: 'Other MCP client',
    sub: 'Any remote MCP client',
    kinds: ['other', 'unknown'],
    what: 'Any client that supports remote (streamable HTTP) MCP servers with OAuth, for example Cline, Zed, Goose or Amazon Q Developer.',
    steps: (url) => [{ text: 'Add a remote MCP server with this URL, at the user (global) level if the client has one, so every project gets it:', code: url }],
    signIn: 'The client asks you to sign in the first time it calls the sandbox. A browser opens:',
    tryPrompt: 'Create a simple hello yellow app in my Flow sandbox',
  },
];

/** A tool's variant by key, else its first (the default); null for a tool with one way only. */
export function variantOf(tool, key) {
  if (!tool.variants?.length) { return null; }
  return tool.variants.find((variant) => variant.key === key) || tool.variants[0];
}

/**
 * The numbered steps of a tool's setup (in the variant `variantKey`): its own steps, then "Sign in" with the same
 * account as Flow and "Check it works" with the live check, unless the tool (or variant) wrote all of them.
 */
export function setupSteps(tool, url, variantKey) {
  const source = variantOf(tool, variantKey) || tool;
  const own = source.steps(url);
  if (source.complete) { return own; }
  return [
    ...own,
    { title: 'Sign in', text: source.signIn || tool.signIn, account: true },
    { title: 'Check it works', text: tool.checkText || `Ask ${tool.name}: “${CHECK_PROMPT}”.`, check: true },
  ];
}

/** Who makes each tool, in the order the picker shows them: the big model makers first, any other MCP client last. */
export const TOOL_VENDORS = [
  { key: 'anthropic', name: 'Anthropic' },
  { key: 'openai', name: 'OpenAI' },
  { key: 'google', name: 'Google' },
  { key: 'microsoft', name: 'Microsoft · GitHub' },
  { key: 'cursor', name: 'Cursor' },
  { key: 'windsurf', name: 'Windsurf' },
  { key: 'jetbrains', name: 'JetBrains' },
  { key: 'mistral', name: 'Mistral AI' },
  { key: 'aws', name: 'AWS' },
  { key: 'other', name: 'Other' },
];

/** AI_TOOLS by vendor, in TOOL_VENDORS order; a tool with an unknown vendor goes under Other. */
export function toolsByVendor(tools = AI_TOOLS) {
  const known = new Set(TOOL_VENDORS.map((vendor) => vendor.key));
  return TOOL_VENDORS
    .map((vendor) => ({ vendor, tools: tools.filter((tool) => (known.has(tool.vendor) ? tool.vendor : 'other') === vendor.key) }))
    .filter((group) => group.tools.length);
}

/** The sandbox overview's entry for a tool when it connected (`aiTools: [{ kind, label, lastSeenAt }]`), else null. */
export function connectionOf(tool, sandbox) {
  return (sandbox?.aiTools || []).find((seen) => tool.kinds.includes(seen.kind)) || null;
}

/** The tool an overview entry (`{ kind }`) belongs to; kinds no tool lists count as Other MCP client. */
export function toolOfKind(kind) {
  return AI_TOOLS.find((tool) => tool.kinds.includes(kind)) || AI_TOOLS.find((tool) => tool.key === 'other');
}

/**
 * The AI tool picker, grouped by vendor: each vendor is a small labelled group of chips, and the groups wrap, so the
 * thirteen tools stay a few lines tall and still read at phone width. One radio group across all of them; a tool
 * that already connected (`sandbox.aiTools`) carries a check.
 */
export function ToolPicker({ value, onChange, sandbox = null, idPrefix = 'tool-picker' }) {
  return (
    <div aria-label="AI tool" className="sbx-tool-picker" role="radiogroup">
      {toolsByVendor().map(({ vendor, tools }) => (
        <div aria-labelledby={`${idPrefix}-${vendor.key}`} className="sbx-tool-group" key={vendor.key} role="group">
          <span className="sbx-tool-vendor" id={`${idPrefix}-${vendor.key}`}>{vendor.name}</span>
          <span className="sbx-tool-chips">
            {tools.map((item) => {
              const on = item.key === value;
              const seen = sandbox && connectionOf(item, sandbox);
              return (
                <button aria-checked={on} className={`sbx-tool-chip${on ? ' on' : ''}`} key={item.key} onClick={() => onChange(item.key)} role="radio" title={seen ? `${item.name} is connected` : undefined} type="button">
                  <ToolIcon icon={item.icon} label={item.name} />{item.name}
                  {seen && <span aria-hidden="true" className="sbx-tool-seen">✓</span>}
                </button>
              );
            })}
          </span>
        </div>
      ))}
    </div>
  );
}

ToolPicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  sandbox: PropTypes.object,
  idPrefix: PropTypes.string,
};

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** A command or config with a Copy button next to it. */
export function CopyCode({ code, copy = null, label = 'Copy' }) {
  const [done, setDone] = useState(false);
  return (
    <div className="sbx-copy-code">
      <code className="sbx-code">{code}</code>
      <Button
        aria-label={label}
        icon={<Copy aria-hidden size={14} />}
        onClick={async() => { if (await copyText(copy ?? code)) { setDone(true); setTimeout(() => setDone(false), 1500); } }}
        size="small"
      >
        {done ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

CopyCode.propTypes = { code: PropTypes.string.isRequired, copy: PropTypes.string, label: PropTypes.string };

/** "Sign in with the same account you use for Flow: ada@example.com." */
export function AccountHint({ email = null }) {
  return (
    <span className="sbx-account">
      Sign in with the <strong>same account you use for Flow</strong>
      {email ? <>: <strong>{email}</strong>.</> : ' (the one you are signed in with on this page).'}
    </span>
  );
}

AccountHint.propTypes = { email: PropTypes.string };

function SetupStep({ step, tool, check, email }) {
  const copyLabel = `Copy${step.title ? `: ${step.title}` : ''}`;
  return (
    <li className="sbx-setup-step">
      {step.title && <strong>{step.title}</strong>}
      {step.text && <span>{step.text}</span>}
      {step.code && <CopyCode code={step.code} copy={step.copy} label={copyLabel} />}
      {step.account && <AccountHint email={email} />}
      {step.options && step.options.map((option, index) => (
        <div className="sbx-setup-option" key={option.label}>
          <strong>{`${String.fromCharCode(97 + index)}) ${option.label}`}</strong>
          {option.text && <span>{option.text}</span>}
          {option.code && <CopyCode code={option.code} label={`Copy: ${option.label}`} />}
          {option.note && <span className="sbx-setup-note">{option.note}</span>}
        </div>
      ))}
      {step.after && <span>{step.after}</span>}
      {step.note && <span className="sbx-setup-note">{step.note}</span>}
      {step.check && <ConnectionCheck hint={tool.notConnectedHint} onCheck={check.refresh} sandbox={check.sandbox} tool={tool} />}
    </li>
  );
}

SetupStep.propTypes = {
  step: PropTypes.object.isRequired,
  tool: PropTypes.object.isRequired,
  check: PropTypes.shape({ sandbox: PropTypes.object, refresh: PropTypes.func }).isRequired,
  email: PropTypes.string,
};

/**
 * The setup of one tool, shared by the dialogs and the first-run setup: numbered steps (setupSteps), the variant
 * toggle when the tool has two ways, the live connection check, and "If it doesn't connect". The check reads the
 * sandbox overview: pass `sandbox` (and `refresh`) when the page already polls it, else it polls every 5 s while shown.
 */
export function ToolInstall({ tool, sandbox, refresh = null, email = null }) {
  const url = sandboxMcpUrl();
  const watched = sandbox !== undefined;
  const own = useSandbox({ intervalMs: 5000, enabled: !watched });
  const check = watched ? { sandbox, refresh } : { sandbox: own.sandbox, refresh: own.refresh };
  const signedIn = useAuthUser();
  const account = email || signedIn?.email || null;
  const [variantByTool, setVariantByTool] = useState({});
  const variant = variantOf(tool, variantByTool[tool.key]);
  const steps = setupSteps(tool, url, variant?.key);
  const troubles = [...(tool.troubleshooting ? tool.troubleshooting(url) : []), ...commonTroubleshooting(url)];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <span style={{ color: 'var(--text-secondary)' }}>{tool.what}</span>
      {tool.cloud && isLocalUrl(url) && (
        <Alert
          message={`${tool.name} connects from the cloud, so it can't reach ${url}. Use the address of a Flow that is reachable from the internet.`}
          showIcon
          type="warning"
        />
      )}
      {tool.variants && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span>Which one is you?</span>
          <Segmented
            aria-label={`How to add Flow to ${tool.name}`}
            className="sbx-variants"
            onChange={(key) => setVariantByTool((previous) => ({ ...previous, [tool.key]: key }))}
            options={tool.variants.map((item) => ({ label: item.label, value: item.key }))}
            value={variant.key}
          />
        </div>
      )}
      {tool.oneClick && (
        <>
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button href={tool.oneClick.href(url)} type="primary">{tool.oneClick.label}</Button>
          </span>
          <span style={{ color: 'var(--text-secondary)' }}>Or set it up by hand:</span>
        </>
      )}
      <ol aria-label={`Set up ${tool.name}`} className="sbx-setup-steps">
        {steps.map((step) => (
          <SetupStep check={check} email={account} key={step.title || step.text} step={step} tool={tool} />
        ))}
      </ol>
      {(tool.notes || []).map((text) => <span className="sbx-setup-note" key={text}>{text}</span>)}
      {tool.note && <span className="sbx-tool-note" style={{ color: 'var(--text-secondary)' }}>{tool.note}</span>}
      {tool.pack && (
        <span>
          <a href={rulesPackUrl(tool.pack)} rel="noreferrer" target="_blank">Rules and commands pack</a>
          <span className="sbx-setup-note"> (optional, in Mia-Platform’s plugin repository: everything works without it)</span>
        </span>
      )}
      <details className="sbx-trouble">
        <summary>If it doesn’t connect</summary>
        <ul>{troubles.map((text) => <li key={text}>{text}</li>)}</ul>
      </details>
    </div>
  );
}

ToolInstall.propTypes = {
  tool: PropTypes.object.isRequired,
  sandbox: PropTypes.object,
  refresh: PropTypes.func,
  email: PropTypes.string,
};

export default function ConnectToolModal({ tool = null, onClose }) {
  return (
    <Modal footer={null} onCancel={onClose} open={Boolean(tool)} title={tool ? <span className="sbx-tool-title"><ToolIcon icon={tool.icon} label={tool.name} size={20} />{`Use your sandbox from ${tool.name}`}</span> : ''}>
      {tool && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <ToolInstall tool={tool} />
          <span>Then ask it, for example:</span>
          <code className="sbx-code">{tool.tryPrompt}</code>
          <span className="sbx-setup-note">Before it picks a language or framework, it asks Flow what is supported (vfs_supported_stacks): <SupportedStacksLink /></span>
        </div>
      )}
    </Modal>
  );
}

ConnectToolModal.propTypes = {
  tool: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

/**
 * "Connect another": the same picker and setup as first-run step 2 (pages/Welcome.jsx), in a dialog. Starts on the
 * first tool that has not connected yet. The setup (and its live check's polling) is there only while it is open.
 */
export function PickToolModal({ open, onClose, sandbox = null }) {
  const firstNew = () => (AI_TOOLS.find((item) => !connectionOf(item, sandbox)) || AI_TOOLS[0]).key;
  const [toolKey, setToolKey] = useState(firstNew);
  const [wasOpen, setWasOpen] = useState(open);
  // Each time it opens, start again from the first tool not connected yet.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setToolKey(firstNew()); }
  }
  const tool = AI_TOOLS.find((item) => item.key === toolKey) || AI_TOOLS[0];
  return (
    <Modal footer={null} onCancel={onClose} open={open} title="Connect an AI tool to your sandbox" width={720}>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ToolPicker idPrefix="pick-tool" onChange={setToolKey} sandbox={sandbox} value={tool.key} />
          <ToolInstall tool={tool} />
          <span>Then ask {tool.name}, for example:</span>
          <code className="sbx-code">{tool.tryPrompt}</code>
          <span className="sbx-setup-note">Before it picks a language or framework, it asks Flow what is supported (vfs_supported_stacks): <SupportedStacksLink /></span>
        </div>
      )}
    </Modal>
  );
}

PickToolModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sandbox: PropTypes.object,
};
